import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useBusiness } from "../context/BusinessContext";
import { api, fmtPrice } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, QrCode, Wallet } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { business } = useBusiness();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    notes: "",
  });
  const [method, setMethod] = useState("billplz");
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image_url: i.image_url || "",
        })),
        payment_method: method,
      };
      const { data: order } = await api.post("/orders", payload);

      if (method === "billplz") {
        const { data: bill } = await api.post("/payments/billplz/create", {
          order_id: order.id,
        });
        clear();
        window.location.href = bill.url;
        return;
      }
      if (method === "stripe") {
        toast.error("Stripe coming soon. Please choose Billplz or QR.");
        setSubmitting(false);
        return;
      }
      if (method === "qr") {
        clear();
        navigate(`/order/${order.id}`);
        return;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed";
      toast.error(typeof msg === "string" ? msg : "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="checkout-page">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-ultra text-neutral-500">Checkout</div>
        <h1 className="text-4xl md:text-6xl font-black tracking-tighter mt-2">Complete order</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="space-y-10">
          <section>
            <h2 className="text-xs uppercase tracking-ultra text-neutral-500 mb-6 border-t border-black pt-6">
              01 / Contact
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full name" required value={form.customer_name} onChange={(v) => update("customer_name", v)} testId="checkout-name" />
              <Field label="Email" type="email" required value={form.customer_email} onChange={(v) => update("customer_email", v)} testId="checkout-email" />
              <Field label="Phone" required value={form.customer_phone} onChange={(v) => update("customer_phone", v)} testId="checkout-phone" />
            </div>
          </section>

          <section>
            <h2 className="text-xs uppercase tracking-ultra text-neutral-500 mb-6 border-t border-black pt-6">
              02 / Shipping
            </h2>
            <Field label="Address" required value={form.address} onChange={(v) => update("address", v)} testId="checkout-address" />
            <div className="mt-6">
              <label className="block text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">
                Order notes (optional)
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="w-full border border-black/10 bg-white p-3 text-sm outline-none focus:border-black"
                rows={3}
                data-testid="checkout-notes"
              />
            </div>
          </section>

          <section>
            <h2 className="text-xs uppercase tracking-ultra text-neutral-500 mb-6 border-t border-black pt-6">
              03 / Payment
            </h2>
            <div className="grid gap-4">
              <PaymentOption
                icon={<Wallet className="w-5 h-5" />}
                title="Billplz"
                description="FPX, e-wallets, Malaysian banks."
                checked={method === "billplz"}
                onSelect={() => setMethod("billplz")}
                testId="payment-method-billplz"
              />
              <PaymentOption
                icon={<CreditCard className="w-5 h-5" />}
                title="Card (Stripe)"
                description="Visa, Mastercard, Amex. Coming soon."
                checked={method === "stripe"}
                onSelect={() => setMethod("stripe")}
                testId="payment-method-stripe"
                disabledBadge="Soon"
              />
              <PaymentOption
                icon={<QrCode className="w-5 h-5" />}
                title="QR payment"
                description="Scan our QR. Upload receipt for manual verification."
                checked={method === "qr"}
                onSelect={() => setMethod("qr")}
                testId="payment-method-qr"
              />
            </div>
          </section>
        </div>

        <aside className="bg-white border border-black/10 p-8 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
          <div className="text-xs uppercase tracking-ultra text-neutral-500 mb-6">Order summary</div>
          <div className="space-y-3 mb-6">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-center justify-between text-sm">
                <div className="flex-1 truncate pr-2">
                  <span className="font-medium">{i.name}</span>{" "}
                  <span className="text-neutral-500">×{i.quantity}</span>
                </div>
                <div className="font-medium">{fmtPrice(i.price * i.quantity, business.currency)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10 pt-4 flex justify-between text-lg font-black">
            <span>Total</span>
            <span data-testid="checkout-total">{fmtPrice(total, business.currency)}</span>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra font-bold hover:bg-neutral-800 transition-colors disabled:opacity-50"
            data-testid="checkout-place-order-button"
          >
            {submitting ? "Processing..." : method === "billplz" ? "Pay with Billplz" : method === "qr" ? "Place order" : "Continue"}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-black bg-transparent py-2 text-sm outline-none focus:border-b-2"
        data-testid={testId}
      />
    </div>
  );
}

function PaymentOption({ icon, title, description, checked, onSelect, testId, disabledBadge }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex items-center gap-4 text-left p-4 border transition-colors ${
        checked ? "border-black bg-black text-white" : "border-black/10 hover:border-black"
      }`}
      data-testid={testId}
    >
      <div>{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-bold flex items-center gap-2">
          {title}
          {disabledBadge && (
            <span className="text-[9px] uppercase tracking-ultra bg-accent text-white px-2 py-0.5">
              {disabledBadge}
            </span>
          )}
        </div>
        <div className={`text-xs mt-1 ${checked ? "text-white/70" : "text-neutral-500"}`}>
          {description}
        </div>
      </div>
      <div
        className={`w-4 h-4 border ${
          checked ? "bg-white border-white" : "border-black"
        }`}
      />
    </button>
  );
}
